import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  Paper,
  Tabs,
  Tab,
  Divider,
  Button,
  Alert,
  Fade,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  CircularProgress,
} from '@mui/material';
import {
  RocketLaunch as RocketIcon,
  LocalOffer as OfferIcon,
  WorkspacePremium as PremiumIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  StarHalf as StarHalfIcon,
  CreditCard as CreditCardIcon,
  Lock as LockIcon,
  Info as InfoIcon,
  SupportAgent as SupportIcon,
  Security as SecurityIcon,
  AutoAwesome as AwesomeIcon,
  Calculate as CalculateIcon,
  CompareArrows as CompareIcon,
  FormatQuote as FormatQuoteIcon,
  Help as HelpIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../redux/store';
import { updateUserData } from '../redux/slices/userSlice';
import { PlanType } from '../redux/slices/userSlice';
import Layout from '../components/Layout';

// Importar os componentes criados
import {
  PlanSimulator,
  PlanComparison,
  Testimonials,
  PlanCards,
  ExpandedFAQ,
  UsageHistory
} from '../components/planos';

// Interface para as propriedades da Tab
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

// Componente TabPanel
const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`plan-tabpanel-${index}`}
      aria-labelledby={`plan-tab-${index}`}
      {...other}
      style={{ width: '100%' }}
    >
      {value === index && (
        <Box sx={{ pt: 3, width: '100%', display: 'flex', justifyContent: 'flex-start', pl: 0 }}>
          <Box sx={{ width: '100%', maxWidth: '1400px', margin: '0', pl: 0 }}>
            {children}
          </Box>
        </Box>
      )}
    </div>
  );
};

// Função para criar props de acessibilidade para as tabs
const a11yProps = (index: number) => {
  return {
    id: `plan-tab-${index}`,
    'aria-controls': `plan-tabpanel-${index}`,
  };
};

// Componente principal da página de planos
const PlanosPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();
  const userData = useSelector((state: RootState) => state.user.data);
  
  // Estados
  const [tabValue, setTabValue] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<PlanType | null>(null);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState<{
    isValid: boolean;
    message: string;
    daysRemaining?: number;
  }>({ isValid: true, message: '' });

  // Verificar o status da assinatura ao carregar a página
  useEffect(() => {
    checkSubscriptionStatus();
  }, [userData.plan, userData.trialEndDate, userData.planFeatures.isTrial]);

  // Função para verificar o status da assinatura
  const checkSubscriptionStatus = () => {
    // Se o usuário está em período de trial
    if (userData.planFeatures.isTrial && userData.trialEndDate) {
      const endDate = new Date(userData.trialEndDate);
      const today = new Date();
      const daysRemaining = Math.ceil((endDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
      
      if (daysRemaining <= 0) {
        setSubscriptionStatus({
          isValid: false,
          message: 'Seu período de teste expirou. Por favor, assine um plano para continuar usando o sistema.',
          daysRemaining: 0
        });
      } else if (daysRemaining <= 3) {
        setSubscriptionStatus({
          isValid: true,
          message: `Seu período de teste expira em ${daysRemaining} dias. Assine um plano para continuar usando o sistema.`,
          daysRemaining
        });
      } else {
        setSubscriptionStatus({
          isValid: true,
          message: `Você está no período de teste. Restam ${daysRemaining} dias.`,
          daysRemaining
        });
      }
    } else if (userData.plan === 'Free') {
      // Se o usuário está no plano gratuito (após expiração do trial)
      setSubscriptionStatus({
        isValid: false,
        message: 'Seu período de teste expirou. Por favor, assine um plano para continuar usando o sistema.'
      });
    } else {
      // Usuário com plano pago
      setSubscriptionStatus({
        isValid: true,
        message: `Você está no plano ${userData.plan}.`
      });
    }
  };

  // Manipuladores de eventos
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handlePlanSelect = (plan: PlanType) => {
    setSelectedPlan(plan);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedPlan(null);
    setPaymentMethod('');
    setError(null);
  };

  const handlePaymentMethodChange = (event: SelectChangeEvent) => {
    setPaymentMethod(event.target.value);
  };

  const handleSubscribe = () => {
    if (!selectedPlan || !paymentMethod) {
      setError('Por favor, selecione um método de pagamento');
      return;
    }

    setLoading(true);
    setError(null);

    // Simulando uma chamada de API para processar o pagamento
    setTimeout(() => {
      try {
        // Atualiza o plano do usuário no Redux
        dispatch(updateUserData({ plan: selectedPlan }));
        setLoading(false);
        handleCloseDialog();
      } catch (err) {
        setError('Ocorreu um erro ao processar seu pagamento. Por favor, tente novamente.');
        setLoading(false);
      }
    }, 2000);
  };

  return (
    <Layout>
      <Box 
        sx={{ 
          background: 'linear-gradient(180deg, rgba(240, 240, 240, 0.5) 0%, rgba(255, 255, 255, 1) 100%)',
          pt: 4,
          pb: 6,
          minHeight: '100vh',
          width: '100%',
          maxWidth: '100%',
          overflow: 'hidden'
        }}
      >
        <Box sx={{ width: '100%' }}>
          {/* Alerta de status da assinatura */}
          {subscriptionStatus.message && (
            <Box 
              sx={{ 
                width: '100%', 
                mb: 3, 
                display: 'flex', 
                justifyContent: 'center' 
              }}
            >
              <Fade in={true} timeout={800}>
                <Alert 
                  severity={subscriptionStatus.isValid ? "info" : "warning"} 
                  sx={{ 
                    borderRadius: '12px',
                    '& .MuiAlert-icon': {
                      fontSize: '1.5rem'
                    },
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                    width: '100%'
                  }}
                  action={
                    !subscriptionStatus.isValid && (
                      <Button 
                        color="inherit" 
                        size="small" 
                        onClick={() => handlePlanSelect('Basic')}
                        sx={{ 
                          fontWeight: 'bold',
                          textTransform: 'none',
                          borderRadius: '8px',
                          px: 2
                        }}
                      >
                        Assinar Agora
                      </Button>
                    )
                  }
                >
                  <Typography variant="body1" fontWeight="medium">
                    {subscriptionStatus.message}
                  </Typography>
                  {subscriptionStatus.daysRemaining !== undefined && subscriptionStatus.daysRemaining > 0 && (
                    <Box sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
                      <Box 
                        sx={{ 
                          width: '100%', 
                          height: '6px', 
                          bgcolor: 'rgba(0, 0, 0, 0.1)', 
                          borderRadius: '3px',
                          overflow: 'hidden'
                        }}
                      >
                        <Box 
                          sx={{ 
                            width: `${(subscriptionStatus.daysRemaining / 15) * 100}%`, 
                            height: '100%', 
                            bgcolor: subscriptionStatus.daysRemaining <= 3 ? 'warning.main' : 'primary.main',
                            borderRadius: '3px'
                          }} 
                        />
                      </Box>
                      <Typography variant="caption" sx={{ ml: 1, fontWeight: 'medium' }}>
                        {subscriptionStatus.daysRemaining} dias restantes
                      </Typography>
                    </Box>
                  )}
                </Alert>
              </Fade>
            </Box>
          )}

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Box sx={{ textAlign: 'left', mb: 6, width: '100%' }}>
              <Typography 
                variant="h3" 
                component="h1" 
                gutterBottom 
                fontWeight="bold" 
                color="primary"
                sx={{ 
                  fontSize: { xs: '1.75rem', md: '2.25rem' },
                  mb: 1,
                  textAlign: 'left'
                }}
              >
                Escolha o Plano Ideal para o Seu Negócio
              </Typography>
              <Typography 
                variant="h6" 
                color="text.secondary" 
                sx={{ 
                  mb: 3, 
                  maxWidth: '800px', 
                  fontSize: { xs: '0.9rem', md: '1.1rem' },
                  textAlign: 'left'
                }}
              >
                Comece com 15 dias de acesso completo e depois escolha o plano que melhor atende às suas necessidades
              </Typography>
              
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'flex-start', 
                gap: 2, 
                flexWrap: 'wrap', 
                mb: 4,
                maxWidth: '1000px'
              }}>
                <Paper 
                  elevation={0} 
                  sx={{ 
                    p: 1.5, 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    borderRadius: '12px',
                    bgcolor: 'rgba(0, 0, 0, 0.03)',
                    minWidth: { xs: '100%', sm: 'auto' }
                  }}
                >
                  <StarIcon color="primary" />
                  <Typography variant="body2" sx={{ textAlign: 'left', fontSize: '0.9rem' }}>15 dias de teste gratuito</Typography>
                </Paper>
                <Paper 
                  elevation={0} 
                  sx={{ 
                    p: 1.5, 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    borderRadius: '12px',
                    bgcolor: 'rgba(0, 0, 0, 0.03)',
                    minWidth: { xs: '100%', sm: 'auto' }
                  }}
                >
                  <SecurityIcon color="primary" />
                  <Typography variant="body2" sx={{ textAlign: 'left', fontSize: '0.9rem' }}>Pagamento seguro</Typography>
                </Paper>
                <Paper 
                  elevation={0} 
                  sx={{ 
                    p: 1.5, 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    borderRadius: '12px',
                    bgcolor: 'rgba(0, 0, 0, 0.03)',
                    minWidth: { xs: '100%', sm: 'auto' }
                  }}
                >
                  <SupportIcon color="primary" />
                  <Typography variant="body2" sx={{ textAlign: 'left', fontSize: '0.9rem' }}>Suporte dedicado</Typography>
                </Paper>
              </Box>
            </Box>
          </motion.div>

          {/* Tabs de navegação */}
          <Box sx={{ width: '100%' }}>
            <Paper 
              elevation={0} 
              sx={{ 
                borderRadius: '16px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                overflow: 'hidden',
              }}
            >
              <Tabs 
                value={tabValue} 
                onChange={handleTabChange} 
                variant={isMobile ? "scrollable" : "fullWidth"}
                scrollButtons={isMobile ? "auto" : false}
                allowScrollButtonsMobile
                sx={{ 
                  bgcolor: 'background.paper',
                  '& .MuiTabs-indicator': {
                    height: 3,
                    borderRadius: '3px 3px 0 0',
                  },
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontWeight: 'medium',
                    fontSize: '0.95rem',
                    minHeight: 64,
                  },
                }}
              >
                <Tab 
                  label="Planos" 
                  icon={<StarIcon />} 
                  iconPosition="start" 
                  {...a11yProps(0)} 
                />
                <Tab 
                  label="Simulador" 
                  icon={<CalculateIcon />} 
                  iconPosition="start" 
                  {...a11yProps(1)} 
                />
                <Tab 
                  label="Comparativo" 
                  icon={<CompareIcon />} 
                  iconPosition="start" 
                  {...a11yProps(2)} 
                />
                <Tab 
                  label="Uso Atual" 
                  icon={<TrendingUpIcon />} 
                  iconPosition="start" 
                  {...a11yProps(3)} 
                />
                <Tab 
                  label="Depoimentos" 
                  icon={<FormatQuoteIcon />} 
                  iconPosition="start" 
                  {...a11yProps(4)} 
                />
                <Tab 
                  label="FAQ" 
                  icon={<HelpIcon />} 
                  iconPosition="start" 
                  {...a11yProps(5)} 
                />
              </Tabs>
            </Paper>

            <TabPanel value={tabValue} index={0}>
              <PlanCards />
            </TabPanel>
            
            <TabPanel value={tabValue} index={1}>
              <PlanSimulator />
            </TabPanel>
            
            <TabPanel value={tabValue} index={2}>
              <PlanComparison />
            </TabPanel>
            
            <TabPanel value={tabValue} index={3}>
              <UsageHistory />
            </TabPanel>
            
            <TabPanel value={tabValue} index={4}>
              <Testimonials />
            </TabPanel>
            
            <TabPanel value={tabValue} index={5}>
              <ExpandedFAQ />
            </TabPanel>
          </Box>
        </Box>
      </Box>

      {/* Dialog de Confirmação de Assinatura */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="sm" 
        fullWidth 
        sx={{ 
          '& .MuiDialog-paper': { 
            borderRadius: '16px',
            overflow: 'hidden'
          } 
        }}
      >
        <DialogTitle 
          sx={{ 
            pb: 1,
            background: selectedPlan === 'Free' 
              ? 'linear-gradient(135deg, #6c757d 0%, #495057 100%)'
              : selectedPlan === 'Basic'
                ? 'linear-gradient(135deg, #3f51b5 0%, #303f9f 100%)'
                : 'linear-gradient(135deg, #f50057 0%, #c51162 100%)',
            color: 'white'
          }}
        >
          <Typography variant="h5" component="div" fontWeight="bold">
            Confirmar Assinatura - Plano {selectedPlan}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <DialogContentText sx={{ mb: 3, fontSize: '1.1rem' }}>
            Você está prestes a assinar o plano {selectedPlan}. Por favor, escolha seu método de pagamento preferido.
          </DialogContentText>
          
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel id="payment-method-label">Método de Pagamento</InputLabel>
            <Select
              labelId="payment-method-label"
              value={paymentMethod}
              onChange={handlePaymentMethodChange}
              label="Método de Pagamento"
            >
              <MenuItem value="credit-card">Cartão de Crédito</MenuItem>
              <MenuItem value="pix">PIX</MenuItem>
              <MenuItem value="boleto">Boleto Bancário</MenuItem>
            </Select>
          </FormControl>
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <Box sx={{ 
            p: 2, 
            bgcolor: 'rgba(0, 0, 0, 0.02)', 
            borderRadius: '8px',
            mb: 2
          }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Resumo da Assinatura
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">Plano:</Typography>
              <Typography variant="body2" fontWeight="medium">{selectedPlan}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">Valor:</Typography>
              <Typography variant="body2" fontWeight="medium">
                R$ {selectedPlan === 'Basic' ? '59,90' : selectedPlan === 'Pro' ? '99,90' : '0,00'}/mês
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Cobrança:</Typography>
              <Typography variant="body2" fontWeight="medium">Mensal</Typography>
            </Box>
          </Box>
          
          <Typography variant="body2" color="text.secondary">
            Ao confirmar, você concorda com nossos termos de serviço e política de privacidade. Você pode cancelar sua assinatura a qualquer momento.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={handleCloseDialog} 
            color="inherit"
            sx={{ 
              fontWeight: 'medium',
              textTransform: 'none',
              borderRadius: '8px',
            }}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleSubscribe} 
            variant="contained" 
            color="primary"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <CreditCardIcon />}
            sx={{ 
              fontWeight: 'bold',
              textTransform: 'none',
              borderRadius: '8px',
              px: 3,
              py: 1,
            }}
          >
            {loading ? 'Processando...' : 'Confirmar Assinatura'}
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default PlanosPage;